import { useClientStore } from '@/stores/client'
import { useProjectStore } from '@/stores/project'
import { useCoreSocket } from '@/services/socket/core-socket'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import * as Sentry from '@sentry/vue'
import i18n from '@/i18n'
import { validateLanguage, type SupportedWidgetLanguage } from '@/helpers/shared'
import { migrateProjectDataParts, migrateProjectRoles } from '@/helpers/project-roles'

interface PostMessageData {
  type?: string
  entry?: string | Record<string, unknown>
  meta?: string | Record<string, unknown>
  source?: string
  __NG_DEVTOOLS_EVENT__?: boolean
}

interface ProjectMeta {
  userId?: string
  parts?: string[]
  roles?: string[]
  language?: string
}

interface ProjectEntry {
  widgetMeta?: {
    language?: string
  }
}

const socket = useCoreSocket()

function applyProjectLanguage(
  languageValue: string | null | undefined,
  userId: string | null | undefined,
  clientStore: ReturnType<typeof useClientStore>
) {
  const language = validateLanguage(languageValue ?? null) as SupportedWidgetLanguage | ''

  if (!language || !clientStore.available_languages.includes(language)) {
    return ''
  }

  i18n.global.locale.value = language
  clientStore.setManualLanguagePreference(language, userId)

  if (socket.messageCenter) {
    socket.messageCenter.triggerMessage('set_language', { language })
    socket.messageCenter.refetchLocalizedContent()
  }

  return language
}

function normalizeProjectPayload(
  meta: ProjectMeta,
  entry?: Record<string, unknown> | null
): { meta: ProjectMeta; entry: Record<string, unknown> | null } {
  const normalizedMeta = {
    ...meta,
    roles: migrateProjectRoles(meta.roles),
    parts: migrateProjectRoles(meta.parts),
  }

  return {
    meta: normalizedMeta,
    entry: entry ? migrateProjectDataParts(entry) : null,
  }
}

export const projectGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const clientStore = useClientStore()
  const projectStore = useProjectStore()
  const controller = new AbortController()

  if (clientStore.authenticated && clientStore.user) {
    if (!projectStore.getProject) {
      window.addEventListener(
        'message',
        (e) => {
          const data = e.data as PostMessageData

          // Ignore non-object messages entirely
          if (!data || typeof data !== 'object') return

          // Ignore common devtools/extension messages
          if (
            data.__NG_DEVTOOLS_EVENT__ === true ||
            (typeof data.source === 'string' && data.source.includes('devtools'))
          ) {
            return
          }

          const type = data?.type
          const allowedTypes = new Set(['inject_data', 'default_data', 'init_success'])

          // Silently ignore any messages we don't recognize
          if (!type || !allowedTypes.has(type)) return

          const expectedOrigin = clientStore.getUser?.origin

          if (type === 'inject_data') {
            if (e.origin !== expectedOrigin) {
              // Report origin mismatch to socket and log for diagnostics
              socket?.messageCenter?.triggerMessage('error', {
                type: 'connection_error',
                message: `Origin mismatch: expected ${expectedOrigin}, got ${e.origin}`,
              })
              Sentry.logger.warn('Origin mismatch on inject_data', { expected: expectedOrigin, received: e.origin })
              return
            }

            let entry: Record<string, unknown> | null = null
            let meta: ProjectMeta | null = null
            try {
              entry =
                typeof data?.entry === 'string'
                  ? JSON.parse(data.entry)
                  : ((data?.entry as Record<string, unknown>) ?? null)
              meta = typeof data?.meta === 'string' ? JSON.parse(data.meta) : ((data?.meta as ProjectMeta) ?? null)
            } catch (err) {
              // Ignore malformed payloads from unexpected sources
              Sentry.logger.warn('Ignoring malformed inject_data payload', { error: String(err) })
              return
            }
            if (meta && meta.userId && entry) {
              // Got existing Project from client
              const language = applyProjectLanguage(
                meta.language ?? (entry as ProjectEntry).widgetMeta?.language,
                meta.userId,
                clientStore
              )
              if (language) {
                meta = { ...meta, language }
              }
              ;({ meta, entry } = normalizeProjectPayload(meta, entry))
              socket?.messageCenter?.triggerMessage('validate_injected_data', { meta, project: entry })
              controller.abort()
              Sentry.metrics.count('widget_event', 1, { attributes: { event_type: type, context: 'project' } })
              Sentry.logger.info('Widget event', {
                event_type: type,
                context: 'project',
                userId: meta.userId,
                parts: meta.parts,
                roles: meta.roles,
              })
            }
            return
          }

          if (type === 'default_data') {
            if (e.origin !== expectedOrigin) {
              // Report origin mismatch to socket and log for diagnostics
              socket?.messageCenter?.triggerMessage('error', {
                type: 'connection_error',
                message: `Origin mismatch: expected ${expectedOrigin}, got ${e.origin}`,
              })
              Sentry.logger.warn('Origin mismatch on default_data', { expected: expectedOrigin, received: e.origin })
              return
            }

            let meta: ProjectMeta | null = null
            try {
              meta = typeof data?.meta === 'string' ? JSON.parse(data.meta) : ((data?.meta as ProjectMeta) ?? null)
            } catch (err) {
              Sentry.logger.warn('Ignoring malformed default_data payload', { error: String(err) })
              return
            }
            if (meta && meta.userId) {
              // Got request for default data from client - meta is required
              const language = applyProjectLanguage(meta.language, meta.userId, clientStore)
              if (language) {
                meta = { ...meta, language }
              }
              ;({ meta } = normalizeProjectPayload(meta))
              socket?.messageCenter?.triggerMessage('get_default_data', { meta })
              controller.abort()
              Sentry.metrics.count('widget_event', 1, { attributes: { event_type: type, context: 'project' } })
              Sentry.logger.info('Widget event', {
                event_type: type,
                context: 'project',
                userId: meta.userId,
                parts: meta.parts,
                roles: meta.roles,
              })
            }
            return
          }

          if (type === 'init_success') {
            if (clientStore.standalone) {
              Sentry.logger.debug('Standalone widget mode active (development only)')
              const meta = {
                userId: 'a1000000-0000-0000-0000-000000000000',
                parts: ['ARCHITECT', 'CIVIL_ENGINEER'],
                roles: ['ARCHITECT', 'CIVIL_ENGINEER'],
                title: 'Widget Test Project',
              }
              socket?.messageCenter?.triggerMessage('get_default_data', { meta })
              controller.abort()
              Sentry.metrics.count('widget_event', 1, { attributes: { event_type: type, context: 'project' } })
              Sentry.logger.info('Widget event', {
                event_type: type,
                context: 'project',
                userId: meta.userId,
                parts: meta.parts,
                roles: meta.roles,
              })
            }
            // Not standalone: ignore init_success from outside
            return
          }
        },
        { signal: controller.signal }
      )

      const socketDataReady = await socket.checkSocketDataReady()
      if (socketDataReady) {
        next()
      } else {
        next({ name: 'error', params: { key: 'errors.general_error' } })
      }
    } else {
      next()
    }
  } else {
    next({ name: 'error', params: { key: 'errors.unknown_error' } })
  }
}
